import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Warning as WarningIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';

interface SidebarProps {
  onItemClick?: () => void;
}

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  restrictedForAssistant?: boolean;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
    restrictedForAssistant: true,
  },
  {
    id: 'lotes',
    label: 'Lotes',
    path: '/lotes',
    icon: <HomeIcon />,
  },
  {
    id: 'reservas-vencidas',
    label: 'Reservas Vencidas',
    path: '/lotes/vencidas',
    icon: <WarningIcon />,
  },
  {
    id: 'reservas-por-vencer',
    label: 'Por Vencer (3 días)',
    path: '/lotes/por-vencer/3',
    icon: <ScheduleIcon />,
  },
  {
    id: 'busqueda',
    label: 'Compradores',
    path: '/busqueda',
    icon: <SearchIcon />,
  },
  {
    id: 'calculadora',
    label: 'Calculadora',
    path: '/calculadora',
    icon: <CalculateIcon />,
    restrictedForAssistant: true,
  },
  {
    id: 'lotificaciones',
    label: 'Lotificaciones',
    path: '/lotificaciones',
    icon: <BusinessIcon />,
  },
];

const adminItems: NavigationItem[] = [
  {
    id: 'reportes-reservas',
    label: 'Reportes Reservas',
    path: '/reportes/reservas',
    icon: <AssessmentIcon />,
    adminOnly: true,
  },
  {
    id: 'reportes-ventas',
    label: 'Reportes Ventas',
    path: '/reportes/ventas',
    icon: <AssessmentIcon />,
    adminOnly: true,
  },
  {
    id: 'usuarios',
    label: 'Usuarios',
    path: '/usuarios',
    icon: <SupervisorAccountIcon />,
    adminOnly: true,
  },
];

const userItems: NavigationItem[] = [
  {
    id: 'perfil',
    label: 'Perfil',
    path: '/perfil',
    icon: <AccountCircleIcon />,
  },
];

export const Sidebar = ({ onItemClick }: SidebarProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    // Special handling for Lotes to avoid overlapping with sub-routes like
    // "/lotes/vencidas" and "/lotes/por-vencer/*" which have their own menu items
    if (path === '/lotes') {
      if (location.pathname.startsWith('/lotes/vencidas')) return false;
      if (location.pathname.startsWith('/lotes/por-vencer')) return false;
      return location.pathname === '/lotes' || location.pathname.startsWith('/lotes/');
    }
    // For other items, consider exact match or nested under their path
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const renderNavigationItem = (item: NavigationItem) => (
    <ListItem key={item.id} disablePadding>
      <ListItemButton
        onClick={() => handleNavigation(item.path)}
        selected={isActive(item.path)}
        sx={{
          borderRadius: 1,
          mx: 1,
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: isActive(item.path) ? 'inherit' : 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            variant: 'body2',
            fontWeight: isActive(item.path) ? 600 : 400,
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color='primary' />
          <Typography variant='h6' color='primary' fontWeight={600}>
            LoteFlow
          </Typography>
        </Box>
      </Toolbar>

      <Divider />

      {/* Main Navigation */}
      <Box sx={{ flex: 1, py: 1 }}>
        <List>
          {navigationItems
            .filter((item) => !(user?.role === 'ASISTENTE' && item.restrictedForAssistant))
            .map(renderNavigationItem)}
        </List>

        {/* Admin Section */}
        {user?.role === 'ADMIN' && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant='overline'
              sx={{ px: 2, color: 'text.secondary', fontWeight: 600 }}
            >
              Administración
            </Typography>
            <List sx={{ pt: 0 }}>{adminItems.map(renderNavigationItem)}</List>
          </>
        )}

        {/* User Section */}
        <Divider sx={{ my: 1 }} />
        <Typography
          variant='overline'
          sx={{ px: 2, color: 'text.secondary', fontWeight: 600 }}
        >
          Usuario
        </Typography>
        <List sx={{ pt: 0 }}>{userItems.map(renderNavigationItem)}</List>
      </Box>

      {/* User Info */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Typography variant='caption' color='text.secondary'>
          Bienvenido
        </Typography>
        <Typography variant='body2' fontWeight={500} noWrap>
          {user?.nombre}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {user?.role}
        </Typography>
      </Box>
    </Box>
  );
};